import { type FormEvent, useCallback, useEffect, useState } from "react";
import {
  type IntegrationProviderCode,
  type OperationsIntegrationProvider,
  type OperationsIntegrationsOverview,
  loadOperationsIntegrations,
  testOperationsIntegrationProvider,
  updateOperationsIntegrationProvider,
  updateOperationsOtpPolicy,
} from "./api.js";

const providerMeta: Readonly<
  Record<
    IntegrationProviderCode,
    {
      readonly title: string;
      readonly description: string;
      readonly secretFields: readonly {
        readonly key: string;
        readonly label: string;
        readonly multiline?: boolean;
      }[];
      readonly configFields: readonly {
        readonly key: string;
        readonly label: string;
        readonly placeholder: string;
      }[];
      readonly testLabel: string;
      readonly testDestinationRequired?: boolean;
    }
  >
> = {
  mobizon_sms: {
    configFields: [
      { key: "baseUrl", label: "API URL", placeholder: "https://api.mobizon.kz" },
      { key: "sender", label: "Имя отправителя", placeholder: "Cleany" },
    ],
    description: "Основной SMS-провайдер для Казахстана.",
    secretFields: [{ key: "apiKey", label: "API key" }],
    testLabel: "Телефон +7…",
    testDestinationRequired: true,
    title: "Mobizon SMS",
  },
  infobip_sms: {
    configFields: [
      {
        key: "baseUrl",
        label: "Персональный API URL",
        placeholder: "https://xxxxx.api.infobip.com",
      },
      { key: "sender", label: "Имя отправителя", placeholder: "Cleany" },
    ],
    description: "Резервный SMS-маршрут и международное расширение.",
    secretFields: [{ key: "apiKey", label: "API key" }],
    testLabel: "Телефон +7…",
    testDestinationRequired: true,
    title: "Infobip SMS",
  },
  meta_whatsapp: {
    configFields: [
      { key: "phoneNumberId", label: "Phone number ID", placeholder: "1234567890" },
      { key: "templateName", label: "Шаблон авторизации", placeholder: "cleany_login_code" },
      { key: "languageCode", label: "Язык шаблона", placeholder: "ru" },
      { key: "graphVersion", label: "Graph API", placeholder: "v24.0" },
    ],
    description: "Резервная доставка кода через утверждённый Meta-шаблон.",
    secretFields: [{ key: "accessToken", label: "Access token" }],
    testLabel: "Телефон WhatsApp +7…",
    testDestinationRequired: true,
    title: "WhatsApp Business",
  },
  telegram_bot: {
    configFields: [{ key: "testChatId", label: "Тестовый chat ID", placeholder: "123456789" }],
    description: "Только уведомления после привязки; не используется для первого входа.",
    secretFields: [{ key: "botToken", label: "Bot token" }],
    testLabel: "Chat ID",
    testDestinationRequired: true,
    title: "Telegram Bot",
  },
  halyk_epay: {
    configFields: [
      { key: "environment", label: "Среда", placeholder: "test или production" },
      { key: "terminalId", label: "Terminal ID", placeholder: "UUID терминала" },
      {
        key: "callbackBaseUrl",
        label: "Public callback URL",
        placeholder: "https://api.cleany.me",
      },
    ],
    description:
      "Авторизация, capture, void и refund. Cleany хранит только CardID, а не реквизиты карты.",
    secretFields: [
      { key: "clientId", label: "Client ID" },
      { key: "clientSecret", label: "Client secret" },
      { key: "secretHash", label: "Postlink secret_hash" },
    ],
    testLabel: "OAuth без списания",
    title: "Halyk ePay",
  },
  twogis_maps: {
    configFields: [
      { key: "locale", label: "Локаль", placeholder: "ru_KZ" },
      { key: "tileset", label: "Набор тайлов", placeholder: "online_sd" },
    ],
    description:
      "Подсказки адресов, геокодирование, тайлы и маршруты с пробками через серверный proxy.",
    secretFields: [{ key: "apiKey", label: "2GIS API key" }],
    testLabel: "Поиск Абая 150",
    title: "2GIS Maps",
  },
  firebase_fcm: {
    configFields: [{ key: "projectId", label: "Firebase project ID", placeholder: "cleany-prod" }],
    description:
      "FCM HTTP v1 для Android и iOS; APNs authentication key загружается в Firebase Console.",
    secretFields: [{ key: "serviceAccountJson", label: "Service account JSON", multiline: true }],
    testLabel: "FCM token (необязательно)",
    title: "Firebase Cloud Messaging",
  },
};

export function OperationsIntegrationsCenter({
  currentRoles,
  token,
}: {
  readonly currentRoles: readonly string[];
  readonly token: string;
}) {
  const [overview, setOverview] = useState<OperationsIntegrationsOverview | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const owner = currentRoles.includes("owner");

  const reload = useCallback(async () => {
    if (!owner) return;
    try {
      setOverview(await loadOperationsIntegrations(token));
      setError("");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Интеграции недоступны");
    }
  }, [owner, token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!owner) {
    return (
      <section className="integrationsCenter">
        <div className="workspaceHeader">
          <div>
            <p className="eyebrow">Secure integrations</p>
            <h1>Каналы и API</h1>
            <p>Настройка провайдеров доступна только владельцу Cleany.</p>
          </div>
        </div>
        <div className="notice">
          Попросите владельца добавить ключи или проверить состояние канала.
        </div>
      </section>
    );
  }

  return (
    <section className="integrationsCenter" aria-labelledby="integrations-title">
      <div className="workspaceHeader adminSectionHeader">
        <div>
          <p className="eyebrow">OTP &amp; messaging control plane</p>
          <h1 id="integrations-title">Каналы и API</h1>
          <p>
            Ключи шифруются на сервере и остаются write-only для браузера. Проверки и изменения
            попадают в аудит.
          </p>
        </div>
        <button className="quietButton" onClick={() => void reload()} type="button">
          Обновить
        </button>
      </div>
      {error && <div className="notice notice--error">{error}</div>}
      {notice && <div className="notice notice--success">{notice}</div>}
      {overview ? (
        <>
          <OtpPolicyCard
            overview={overview}
            onSaved={async () => {
              setNotice("Политика OTP обновлена.");
              await reload();
            }}
            token={token}
          />
          <div className="integrationProviderGrid">
            {overview.providers
              .filter((provider) => provider.code !== "halyk_epay")
              .map((provider) => (
                <ProviderCard
                  key={provider.code}
                  onSaved={async (message) => {
                    setNotice(message);
                    await reload();
                  }}
                  provider={provider}
                  token={token}
                />
              ))}
          </div>
          <DeliveryJournal overview={overview} />
        </>
      ) : !error ? (
        <div className="panel integrationsLoading">Загружаем защищённые настройки…</div>
      ) : null}
    </section>
  );
}

function OtpPolicyCard({
  overview,
  token,
  onSaved,
}: {
  readonly overview: OperationsIntegrationsOverview;
  readonly token: string;
  readonly onSaved: () => Promise<void>;
}) {
  const [primaryChannel, setPrimaryChannel] = useState(overview.policy.primaryChannel);
  const [fallback, setFallback] = useState(overview.policy.whatsappFallbackToSms);
  const [ttl, setTtl] = useState(overview.policy.codeTtlSeconds);
  const [resend, setResend] = useState(overview.policy.resendAfterSeconds);
  const [attempts, setAttempts] = useState(overview.policy.maxVerifyAttempts);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await updateOperationsOtpPolicy(token, {
        codeTtlSeconds: ttl,
        maxVerifyAttempts: attempts,
        primaryChannel,
        reason,
        resendAfterSeconds: resend,
        whatsappFallbackToSms: fallback,
      });
      setReason("");
      setError("");
      await onSaved();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Политика не сохранена");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel otpPolicyCard" onSubmit={(event) => void submit(event)}>
      <div className="integrationCardHeading">
        <div>
          <p className="cardLabel">Политика входа</p>
          <h2>SMS по умолчанию, WhatsApp как резерв</h2>
        </div>
        <span className="secureBadge">Шифрование включено</span>
      </div>
      <div className="otpPolicyFields">
        <label>
          Основной канал
          <select
            onChange={(event) => setPrimaryChannel(event.target.value as "sms" | "whatsapp")}
            value={primaryChannel}
          >
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </label>
        <label>
          Код действует, сек.
          <input
            min={120}
            max={600}
            onChange={(event) => setTtl(Number(event.target.value))}
            type="number"
            value={ttl}
          />
        </label>
        <label>
          Повтор через, сек.
          <input
            min={30}
            max={300}
            onChange={(event) => setResend(Number(event.target.value))}
            type="number"
            value={resend}
          />
        </label>
        <label>
          Попыток ввода
          <input
            min={3}
            max={10}
            onChange={(event) => setAttempts(Number(event.target.value))}
            type="number"
            value={attempts}
          />
        </label>
      </div>
      <label className="integrationToggle">
        <input
          checked={fallback}
          onChange={(event) => setFallback(event.target.checked)}
          type="checkbox"
        />
        <span>Если WhatsApp недоступен, отправить тот же запрос через SMS</span>
      </label>
      <div className="integrationActionRow">
        <input
          aria-label="Причина изменения OTP"
          onChange={(event) => setReason(event.target.value)}
          placeholder="Причина изменения (обязательно)"
          value={reason}
        />
        <button disabled={busy || reason.trim().length < 8} type="submit">
          Сохранить политику
        </button>
      </div>
      {error && <small className="fieldError">{error}</small>}
    </form>
  );
}

function ProviderCard({
  provider,
  token,
  onSaved,
}: {
  readonly provider: OperationsIntegrationProvider;
  readonly token: string;
  readonly onSaved: (message: string) => Promise<void>;
}) {
  const meta = providerMeta[provider.code];
  const [enabled, setEnabled] = useState(provider.enabled);
  const [priority, setPriority] = useState(provider.priority);
  const [config, setConfig] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      meta.configFields.map((field) => [field.key, stringValue(provider.publicConfig[field.key])]),
    ),
  );
  const [secrets, setSecrets] = useState<Record<string, string>>(() =>
    Object.fromEntries(meta.secretFields.map((field) => [field.key, ""])),
  );
  const [reason, setReason] = useState("");
  const [destination, setDestination] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await updateOperationsIntegrationProvider(token, provider.code, {
        enabled,
        priority,
        publicConfig: config,
        reason,
        ...(Object.values(secrets).some((value) => value.trim())
          ? {
              secrets: Object.fromEntries(
                Object.entries(secrets)
                  .map(([key, value]) => [key, value.trim()] as const)
                  .filter((entry) => entry[1]),
              ),
            }
          : {}),
      });
      setSecrets(Object.fromEntries(meta.secretFields.map((field) => [field.key, ""])));
      setReason("");
      setMessage("Настройки сохранены. Выполните тест канала.");
      await onSaved(`${meta.title}: настройки сохранены.`);
    } catch (failure) {
      setMessage(failure instanceof Error ? failure.message : "Настройки не сохранены");
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    try {
      await testOperationsIntegrationProvider(token, provider.code, destination.trim());
      setMessage("Тест доставлен. Канал работает.");
      await onSaved(`${meta.title}: тест успешно доставлен.`);
    } catch (failure) {
      setMessage(failure instanceof Error ? failure.message : "Тест не выполнен");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel integrationProviderCard" onSubmit={(event) => void save(event)}>
      <div className="integrationCardHeading">
        <div>
          <p className="cardLabel">{provider.channel}</p>
          <h2>{meta.title}</h2>
        </div>
        <ProviderHealth provider={provider} />
      </div>
      <p>{meta.description}</p>
      <label className="integrationToggle">
        <input
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
          type="checkbox"
        />
        <span>Канал включён</span>
      </label>
      <div className="integrationFormGrid">
        {meta.configFields.map((field) => (
          <label key={field.key}>
            {field.label}
            <input
              onChange={(event) =>
                setConfig((current) => ({ ...current, [field.key]: event.target.value }))
              }
              placeholder={field.placeholder}
              value={config[field.key] ?? ""}
            />
          </label>
        ))}
        <label>
          Приоритет
          <input
            min={1}
            max={1000}
            onChange={(event) => setPriority(Number(event.target.value))}
            type="number"
            value={priority}
          />
        </label>
        {meta.secretFields.map((field) => {
          const controlId = `${provider.code}-${field.key}`;
          return (
            <label htmlFor={controlId} key={field.key}>
              {field.label}
              {field.multiline ? (
                <textarea
                  autoComplete="off"
                  id={controlId}
                  onChange={(event) =>
                    setSecrets((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                  placeholder={
                    provider.secretConfigured
                      ? "Уже сохранён · вставьте JSON только для замены"
                      : "Вставьте JSON service account"
                  }
                  rows={5}
                  value={secrets[field.key] ?? ""}
                />
              ) : (
                <input
                  autoComplete="new-password"
                  id={controlId}
                  onChange={(event) =>
                    setSecrets((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                  placeholder={
                    provider.secretConfigured
                      ? "Уже сохранён · введите только для замены"
                      : "Добавьте секрет"
                  }
                  type="password"
                  value={secrets[field.key] ?? ""}
                />
              )}
            </label>
          );
        })}
      </div>
      <label className="integrationReason">
        Причина изменения
        <input
          onChange={(event) => setReason(event.target.value)}
          placeholder="Например: подключение production-аккаунта"
          value={reason}
        />
      </label>
      <div className="integrationCardActions">
        <button
          disabled={
            busy ||
            reason.trim().length < 8 ||
            (enabled &&
              !provider.secretConfigured &&
              !Object.values(secrets).some((value) => value.trim()))
          }
          type="submit"
        >
          Сохранить
        </button>
        <div>
          <input
            aria-label={meta.testLabel}
            onChange={(event) => setDestination(event.target.value)}
            placeholder={meta.testLabel}
            value={destination}
          />
          <button
            className="quietButton"
            disabled={
              busy ||
              !provider.secretConfigured ||
              (meta.testDestinationRequired === true && !destination.trim())
            }
            onClick={() => void test()}
            type="button"
          >
            Тест
          </button>
        </div>
      </div>
      {message && <small className="integrationInlineStatus">{message}</small>}
    </form>
  );
}

function ProviderHealth({ provider }: { readonly provider: OperationsIntegrationProvider }) {
  const label = {
    failed: "Ошибка",
    healthy: "Работает",
    not_configured: "Нет ключа",
    untested: "Нужен тест",
  }[provider.healthStatus];
  return <span className={`providerHealth providerHealth--${provider.healthStatus}`}>{label}</span>;
}

function DeliveryJournal({ overview }: { readonly overview: OperationsIntegrationsOverview }) {
  const delivered = overview.deliveries.filter((item) => item.status === "sent").length;
  const successRate = overview.deliveries.length
    ? Math.round((delivered / overview.deliveries.length) * 100)
    : null;
  return (
    <article className="panel deliveryJournal">
      <div className="integrationCardHeading">
        <div>
          <p className="cardLabel">Последние 50 попыток</p>
          <h2>Журнал доставки OTP</h2>
        </div>
        <strong>{successRate === null ? "Нет данных" : `${successRate}% принято`}</strong>
      </div>
      <div className="deliveryTable">
        {overview.deliveries.map((delivery) => (
          <div className="deliveryRow" key={delivery.id}>
            <span className={`deliveryMark deliveryMark--${delivery.status}`} aria-hidden="true" />
            <div>
              <strong>{delivery.destinationMasked}</strong>
              <small>
                {delivery.application === "partner" ? "Партнёр" : "Клиент"} · {delivery.channel}
              </small>
            </div>
            <div>
              <strong>{delivery.providerCode}</strong>
              <small>{delivery.durationMs} мс</small>
            </div>
            <div>
              <strong>{delivery.status === "sent" ? "Передано" : "Ошибка"}</strong>
              <small>{delivery.failureCode ?? formatDateTime(delivery.createdAt)}</small>
            </div>
          </div>
        ))}
        {overview.deliveries.length === 0 && (
          <p className="emptyDeliveryState">
            После первого запроса OTP здесь появится обезличенная диагностика.
          </p>
        )}
      </div>
    </article>
  );
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("ru-KZ", { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
}
